import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Save, Eye, FileText } from 'lucide-react';
import './LegalEditor.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AboutEditor = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/about-content`);
      setContent(response.data.content || getDefaultTemplate());
    } catch (error) {
      console.error('Error loading about content:', error);
      setContent(getDefaultTemplate());
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    if (!content.trim()) {
      toast.error('Inhalt darf nicht leer sein');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/about-content`, {
        content: content
      });

      toast.success('Erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving about content:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getDefaultTemplate = () => {
    return `<h1>About Debatefy</h1>
<p>
  <strong>Debatefy</strong> is an innovative AI-moderated debate platform that 
  promotes fact-based and solution-oriented discussions between two participants.
</p>

<h2>Our Vision</h2>
<p>
  We believe in the power of constructive dialogue. Debatefy creates a space 
  where different perspectives can meet without focusing on rhetorical victories. 
  Instead, we promote insight, understanding, and mutual growth.
</p>

<h2>What Sets Us Apart</h2>
<ul>
  <li><strong>AI Moderation:</strong> Neutral oversight by advanced AI models</li>
  <li><strong>Fact-Checking:</strong> Real-time verification by Perplexity AI</li>
  <li><strong>Insight Scoring:</strong> Rewarding depth over rhetoric</li>
  <li><strong>Split-Timer:</strong> Time to think without pressure</li>
  <li><strong>Multilingual:</strong> Support for German, English, Spanish, French, and Italian</li>
</ul>

<h2>Contact & Legal</h2>
<p>
  For legal information and imprint, please see our Terms and Privacy Policy pages.
</p>

<p class="text-sm text-muted-foreground mt-8">
  © 2025 Debatefy.online - Where minds meet to grow
</p>`;
  };

  const previewContent = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>About Vorschau</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 900px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.8;
            }
            h1 { color: #108ADF; font-size: 32px; }
            h2 { color: #666; font-size: 22px; margin-top: 25px; }
            p { margin-bottom: 15px; }
            ul { margin-left: 30px; }
            li { margin-bottom: 8px; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    newWindow.document.close();
  };

  return (
    <div className="legal-editor-container">
      <Card>
        <CardHeader>
          <CardTitle>
            <FileText className="inline mr-2" size={24} />
            About-Seite verwalten
          </CardTitle>
          <CardDescription>
            Bearbeiten Sie den Inhalt der About-Seite
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="editor-loading">Lädt...</div>
          ) : (
            <>
              <div className="editor-info">
                <p>💡 Sie können HTML-Tags verwenden: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;</p>
              </div>
              <textarea
                className="legal-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Inhalt hier eingeben..."
                rows={25}
              />
            </>
          )}

          {/* Action Buttons */}
          <div className="editor-actions">
            <Button
              onClick={previewContent}
              variant="outline"
              disabled={loading}
            >
              <Eye size={18} className="mr-2" />
              In neuem Tab öffnen
            </Button>
            
            <Button
              onClick={saveContent}
              disabled={saving || loading || !content.trim()}
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AboutEditor;
